<template>
    <div class="z-index-2">
      <div class="p-3 pb-0">
        <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
            {{ dateRange }}
        </div>
        <h5>{{ title }}</h5>
      </div>
      <div class="p-3 pt-2">
        <div class="chart">
          <canvas :id="id" class="chart-canvas" :height="height"></canvas>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import Utils from "@/helpers/utils.js";
  import Chart from "chart.js/auto";
  import annotationPlugin from "chartjs-plugin-annotation";
  import styles from "./UserStepsChart.vue?vue&type=style&index=0&lang=scss&module=1";
  
  Chart.register(annotationPlugin);
  
  export default {
    name: "UserStepsChart",
    props: {
      id: {
        type: String,
        default: "user-steps-chart",
      },
      title: {
        type: String,
        default: "Steps",
      },
      height: {
        type: String,
        default: "300",
      },
      chartData: {
        type: Object,
        required: true,
      },
      dateRange: {
        type: String,
        required: true,
      }
    },
    data() {
      return {
        chart: {},
      };
    },
    watch: {
      chartData() {
        this.addChart();
      },
    },
    mounted() {
      Chart.defaults.borderColor = styles.darkGray;
      this.addChart();
    },
    methods: {
      addChart() {
        this.chart = {
            labels: this.chartData.chartLabels,
            datasets:[
                {  
                    label:"Steps",
                    data: this.chartData.steps
                }
            ]
        };

        const ctx = document.getElementById(this.id).getContext("2d");
        let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
  
        gradientStroke1.addColorStop(1, "rgba(172,0,0,0.15)");
        gradientStroke1.addColorStop(0, "transparent");
  
        let chartStatus = Chart.getChart(this.id);
        if (chartStatus != undefined) {
          chartStatus.destroy();
        }
  
        new Chart(ctx, {
          data: {
            labels: this.chart.labels,
            datasets: [
              {
                type: "bar",
                label: this.chart.datasets[0].label,
                weight: 5,
                tension: 0.4,
                borderWidth: 0,
                pointBackgroundColor: styles.colorSteps,
                borderColor: styles.colorSteps,
                backgroundColor: styles.colorSteps,
                borderRadius: 4,
                borderSkipped: false,
                data: this.chart.datasets[0].data,
                maxBarThickness: 30,
                order: 2,
              }
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              annotation: {
                annotations: {
                 line: {
                  type: "line",
                  borderColor: styles.colorLimitline,
                  borderDash: [6, 6],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    content: (ctx) =>
                      `Avg: ${Utils.numberWithCommas(Math.round(this.average(ctx)))}`,
                    position: "start",
                  },
                  scaleID: "y",
                  value: (ctx) => Math.round(this.average(ctx)),
                }
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  color: styles.colorAxes,
                  boxWidth: 12,
                  boxHeight: 12,
                  padding: 40,
                },
              },
            },
            interaction: {
              intersect: false,
              mode: "index",
            },
            scales: {
              y: {
                display: true,
                position: "left",
                scaleLabel: {
                    display: true,
                    labelString: '1k = 1000'
                },
                ticks: {
                    callback: function(label) {
                       return label/1000+'k';
                    },
                    stepSize: 100,
                    color: styles.gray,
                },
                title: {
                  display: true,
                  text: this.chart.datasets[0].label,
                  color: styles.colorAxes,
                  font: {
                    family: "NotoSans-Regular",
                  },
                },
                gridLines: {
                  drawTicks: false,
                  display: false
                }
              },
              x: {
                ticks: {
                  display: true,
                  color: styles.gray,
                  padding: 10,
                  font: {
                    size: 12,
                    style: "normal",
                    lineHeight: 2,
                  },
                },
                gridLines: {
                  zeroLineColor: "transparent"
                },
              }
            },
          },
        });
      },
      average(ctx) {
        const values = ctx.chart.data.datasets[0].data;
        return values.reduce((a, b) => a + b, 0) / values.length;
      }
    },
  };
  </script>
  <style lang="scss" scoped>
  :export {
    darkRed: $g-red_900;
    red: $g-red_500;
    gray: $color-muted-text;
    darkGray: $color-gridlines;
    colorSteps: $g-red_100;
    colorAxes: $color-chart-axes-text;
    colorLimitline: $color-chart-limitline;
   }
   .date-range {
    color: #bccdcb;
    font-weight: 100;
    font-size: 0.75rem;
   }
  </style>
  